import { z } from "zod";

/**
 * Error catalog of the Explorer API (`/explorer/v1/*`, design D4; spec explorer-access). Codes are
 * stable and never translated; the console login refusals are those of `CONSOLE_AUTH_ERRORS`. It is
 * a catalog of its own: these codes are never answered by the Token or Verify APIs.
 */
export const EXPLORER_API_ERRORS = {
  invalid_request: { status: 400, description: "Request fields are missing or invalid." },
  invalid_reset_code: {
    status: 400,
    description:
      "The password reset code is wrong, expired, already used or replaced (the same answer for an email that got no code); ask for a new code.",
  },
  invalid_credentials: {
    status: 401,
    description: "Email or password is wrong (the same answer for an unknown email).",
  },
  unauthenticated: {
    status: 401,
    description: "No live console session (missing, ended, idle for 30 min or past 12 h).",
  },
  sso_failed: { status: 401, description: "The SSO login could not be completed." },
  invalid_mfa_code: {
    status: 401,
    description:
      "The authenticator code is wrong or was already used; it counts as a failed login.",
  },
  mfa_challenge_expired: {
    status: 401,
    description:
      "The code step (3 minutes), the new-password step (3 minutes) or the set-up step (10 minutes) expired, 3 wrong attempts were made, or it belongs to another browser; enter the password again.",
  },
  forbidden: { status: 403, description: "The console user lacks the permission code." },
  csrf_token_invalid: {
    status: 403,
    description: "The X-CSRF-Token header is missing or does not match the session.",
  },
  sso_required: {
    status: 403,
    description: "The organization signs in through its identity provider (SSO).",
  },
  mfa_reset_pending: {
    status: 403,
    description:
      "The two-factor authentication was reset; use the recovery invitation from an Administrator.",
  },
  not_found: {
    status: 404,
    description: "The resource does not exist or is outside the console user's scope.",
  },
  conflict: { status: 409, description: "The request conflicts with the current state." },
  already_blocked: {
    status: 409,
    description: "The target already has an active block in this environment.",
  },
  block_not_active: { status: 409, description: "Only an active block can be unblocked." },
  export_not_ready: {
    status: 409,
    description: "The export is still being generated (or failed); wait for its notification.",
  },
  export_limit_reached: {
    status: 409,
    description:
      "The console user already has 3 background exports being generated; wait for one to finish.",
  },
  sso_not_available: {
    status: 409,
    description: "SSO is not available for this account (no active identity provider).",
  },
  invitation_used: { status: 409, description: "The invitation was already used." },
  two_factor_required: {
    status: 409,
    description:
      "Two-factor authentication is mandatory for this console user; replace the authenticator instead.",
  },
  last_administrator: {
    status: 409,
    description: "The operator's last active Administrator is protected.",
  },
  own_account_protected: {
    status: 409,
    description:
      "A console user cannot deactivate their own account, remove their own access or give themselves a role lower than Administrator.",
  },
  last_platform_administrator: {
    status: 409,
    description:
      "A Platform Administrator cannot deactivate themselves or the last active Platform Administrator.",
  },
  credential_limit_reached: {
    status: 409,
    description: "The operator environment already has 2 active API credentials.",
  },
  identity_provider_not_tested: {
    status: 409,
    description: "The identity provider needs a successful test since its last change.",
  },
  operator_deactivated: {
    status: 409,
    description:
      "The operator is deactivated (terminal): its configuration is read-only, and no access or invitation can be given on it.",
  },
  invitation_expired: {
    status: 410,
    description: "The invitation has expired; ask for a new one.",
  },
  invitation_revoked: { status: 410, description: "The invitation was revoked." },
  export_expired: {
    status: 410,
    description: "The export file is no longer available (kept 7 days); request a new export.",
  },
  weak_password: {
    status: 422,
    description: "The password breaks the password policy (the rule is in details).",
  },
  account_locked: {
    status: 423,
    description: "Too many failed logins; the account is locked for the remaining seconds.",
  },
  step_up_rate_limited: {
    status: 429,
    description:
      "Too many step-up confirmations in flight or failed in 15 minutes (Fleet tab); retry after the Retry-After seconds.",
  },
  rate_limited: {
    status: 429,
    description: "Too many requests; retry after the Retry-After seconds.",
  },
  server_error: {
    status: 503,
    // the API Gateway answers this text too (infra/cdk console-edge); the password_changed
    // exception is documented on details.reason and on the password reset route
    description: "A dependency needed to decide safely is unavailable; nothing was done.",
  },
} as const satisfies Record<string, { status: number; description: string }>;

export type ExplorerApiErrorCode = keyof typeof EXPLORER_API_ERRORS;
export const EXPLORER_API_ERROR_CODES = Object.keys(EXPLORER_API_ERRORS) as ExplorerApiErrorCode[];

export const ExplorerApiErrorCodeSchema = z
  .enum(EXPLORER_API_ERROR_CODES as [ExplorerApiErrorCode, ...ExplorerApiErrorCode[]])
  .meta({ id: "ExplorerApiErrorCode" });

/**
 * Domain reasons answered as `invalid_request` + `details.reason` by the Explorer API (the error
 * mapper's validation codes plus the reasons of period, area, note, export and column validation).
 * The console maps each one to a message; the field stays lenient (`enum | string`) because other
 * `invalid_request` answers carry free-text reasons.
 */
export const EXPLORER_INVALID_REQUEST_REASONS = [
  "invalid_block_reason",
  "comment_too_short",
  "comment_too_long",
  "expiry_not_in_future",
  "invalid_target_kind",
  "invalid_target_ref",
  "unblock_reason_required",
  "unblock_reason_too_long",
  "invalid_column_preference",
  "invalid_period",
  "invalid_zoom",
  "area_too_large",
  "note_empty",
  "note_too_long",
  "invalid_player_ref",
  "invalid_export_selection",
] as const;
export type ExplorerInvalidRequestReason = (typeof EXPLORER_INVALID_REQUEST_REASONS)[number];
export const ExplorerInvalidRequestReasonSchema = z
  .enum(EXPLORER_INVALID_REQUEST_REASONS)
  .meta({ id: "ExplorerInvalidRequestReason" });

export function isExplorerInvalidRequestReason(
  value: string,
): value is ExplorerInvalidRequestReason {
  return (EXPLORER_INVALID_REQUEST_REASONS as readonly string[]).includes(value);
}

/**
 * Reasons a `server_error` may carry in `details.reason`. Without one, nothing changed. With
 * `password_changed` (`POST /auth/password-reset/confirm` only), the identity store set the new
 * password before ending the sessions or recording the reset failed: the user signs in with the new
 * password, and the sessions are still ended (verify-console-account-settings design D2).
 */
export const EXPLORER_SERVER_ERROR_REASONS = ["password_changed"] as const;
export type ExplorerServerErrorReason = (typeof EXPLORER_SERVER_ERROR_REASONS)[number];

/** Password policy rule a refused password breaks (`weak_password`). */
export const PasswordRuleV1 = z
  .enum(["min_length", "common_password", "compromised_password"])
  .meta({ id: "PasswordRuleV1" });

/** Error body of the Explorer API. `details` never carries internals, secrets or stack traces. */
export const ExplorerApiError = z
  .object({
    error: ExplorerApiErrorCodeSchema,
    message: z.string(),
    details: z
      .object({
        /** `invalid_request`: the invalid fields. */
        fields: z.array(z.string()).optional(),
        /**
         * `conflict`, `sso_failed`: a stable reason such as `console_user_exists`;
         * `invalid_request` of a domain rule: its code (e.g. `comment_too_short`);
         * `server_error` of a password reset confirmation: `password_changed` when the new
         * password was set before the failure (`EXPLORER_SERVER_ERROR_REASONS`).
         */
        reason: ExplorerInvalidRequestReasonSchema.or(z.string()).optional(),
        /**
         * `server_error` of a Fleet change whose dispatch failed: the recorded request, ready and
         * unused, to dispatch again (`POST /platform/fleet/requests/{id}/dispatch`).
         */
        request_id: z.string().max(64).optional(),
        /** `conflict` of the Fleet API: the rule's explanation, for the Platform Administrator. */
        explanation: z.string().max(500).optional(),
        /** `account_locked`: remaining lock time. */
        locked_for_seconds: z.int().positive().optional(),
        /** `weak_password`: the broken rule and the minimum length. */
        rule: PasswordRuleV1.optional(),
        min_length: z.int().positive().optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .meta({ id: "ExplorerApiError" });
export type ExplorerApiError = z.infer<typeof ExplorerApiError>;

export function explorerApiError(
  error: ExplorerApiErrorCode,
  details?: ExplorerApiError["details"],
): ExplorerApiError {
  return {
    error,
    message: EXPLORER_API_ERRORS[error].description,
    ...(details === undefined ? {} : { details }),
  };
}
